import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import subscriptionService from '../../services/subscriptionService';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addToast } from '../../store/slices/uiSlice';

const PAGE_OWNED_RETURN_PATHS = ['/profile/', '/stream/fullscreen/', '/watch/'];

export const SubscriptionCheckoutReturnHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const handledSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || PAGE_OWNED_RETURN_PATHS.some((path) => location.pathname.startsWith(path))) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('subscription');
    const checkoutSessionId = params.get('session_id');

    if (!checkoutStatus) {
      return;
    }

    const clearReturnParams = () => {
      params.delete('subscription');
      params.delete('session_id');
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    };

    if (checkoutStatus === 'cancelled') {
      clearReturnParams();
      dispatch(addToast({ type: 'info', message: 'Subscription checkout was cancelled.' }));
      return;
    }

    if (checkoutStatus !== 'success' || !checkoutSessionId || handledSessionRef.current === checkoutSessionId) {
      return;
    }

    handledSessionRef.current = checkoutSessionId;
    dispatch(addToast({ type: 'info', message: 'Confirming subscription...' }));

    void subscriptionService.syncSubscriptionCheckoutSession(checkoutSessionId)
      .then(() => {
        dispatch(addToast({ type: 'success', message: 'Subscription activated.' }));
      })
      .catch((error) => {
        console.error('Failed to sync subscription checkout:', error);
        dispatch(addToast({ type: 'error', message: 'Payment returned, but subscription sync is still pending.' }));
      })
      .finally(clearReturnParams);
  }, [dispatch, isAuthenticated, location.pathname, location.search, navigate]);

  return null;
};

export default SubscriptionCheckoutReturnHandler;
